import { useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import { CampaignPartiallySavedError, objectivesFor, type CampaignDraft, type SaveIntent } from '@/api/campaigns'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, EmptyState, LoadingBlock } from '@/design'
import { usePageFreshness } from '@/lib/freshness'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { IssueList } from '../fields'
import { errorMessage, useCampaignMutation, useCampaignWebsites, useFilterOptions, useMoney } from '../hooks'
import { STEP_BODIES, STEP_DESCRIPTIONS } from './steps'
import {
  canSave,
  draftIssues,
  initialWizardState,
  issuesFor,
  REVIEW_STEP,
  WIZARD_STEPS,
  wizardReducer,
  type StepKey,
} from './state'
import '../campaigns.css'

/** Issues about a value someone has typed show at once; "you have not filled this in" waits until they try to move on. */
const EMPTY_FIELDS = new Set(['name', 'websiteId', 'objective', 'customObjective', 'channels'])

/**
 * The nine-step create flow (pivot doc §6 Stage 2): basics, objective, channels
 * online and offline, audience, budget, creative, per-channel tracking,
 * automation, and a review that highlights anything missing before the
 * campaign is saved as a draft or launched.
 */
export function CampaignWizardPage({ route }: { route: RouteDef }) {
  const client = useApiClient()
  const navigate = useNavigate()
  const search = useFilterSearch()
  const money = useMoney()
  const websites = useCampaignWebsites()
  const filterOptions = useFilterOptions()
  usePageFreshness(client.kind === 'mock' ? { source: 'demo', lastUpdatedAt: null } : null)

  const [state, dispatch] = useReducer(wizardReducer, undefined, () => initialWizardState())
  const [saveError, setSaveError] = useState<string | null>(null)
  /** Set when a save stopped part-way: the draft exists, and saving again would duplicate it. */
  const [savedDraftId, setSavedDraftId] = useState<string | null>(null)
  const create = useCampaignMutation((draft: CampaignDraft, intent: SaveIntent) => client.createCampaign(draft, intent))
  const issues = useMemo(() => draftIssues(state.draft), [state.draft])

  // Default to the production website once the list arrives.
  const websiteList = websites.data
  useEffect(() => {
    if (state.draft.websiteId || !websiteList?.length) return
    const preferred = websiteList.find((website) => website.environment === 'production') ?? websiteList[0]
    if (preferred) dispatch({ type: 'setBasics', patch: { websiteId: preferred.id } })
  }, [websiteList, state.draft.websiteId])

  // Move focus to the step's heading when the step changes, so keyboard and screen-reader users land on it.
  const heading = useRef<HTMLDivElement>(null)
  const firstStep = useRef(true)
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false
      return
    }
    heading.current?.querySelector<HTMLElement>('[tabindex="-1"]')?.focus()
  }, [state.step])

  if (websites.isPending) {
    return (
      <SectionFrame route={route}>
        <LoadingBlock rows={6} />
      </SectionFrame>
    )
  }
  if (websites.isError) {
    return (
      <SectionFrame route={route}>
        <Card>
          <EmptyState variant="outage" description="The websites this campaign could point at could not be loaded. Try again shortly." />
        </Card>
      </SectionFrame>
    )
  }

  const current = WIZARD_STEPS[state.step] ?? WIZARD_STEPS[0]
  const stepKey: StepKey = current.key
  const attempted = state.attempted.includes(stepKey)
  const stepIssues = issuesFor(issues, stepKey)
  const fieldError = (field: string) => {
    const issue = stepIssues.find((candidate) => candidate.field === field && candidate.severity !== 'warning')
    if (!issue) return null
    const name = field.includes('.') ? (field.split('.').pop() as string) : field
    return attempted || !EMPTY_FIELDS.has(name) ? issue.message : null
  }
  const Body = STEP_BODIES[stepKey]
  const blockingHere = stepIssues.filter((issue) => issue.severity !== 'warning')

  async function save(intent: SaveIntent) {
    setSaveError(null)
    try {
      const result = await create.mutateAsync([state.draft, intent])
      navigate({ pathname: `/marketing/campaigns/${encodeURIComponent(result.data.campaign.id)}`, search })
    } catch (error) {
      if (error instanceof CampaignPartiallySavedError) setSavedDraftId(error.campaignId)
      setSaveError(errorMessage(error, 'The campaign could not be saved. Try again.'))
    }
  }

  const onReview = state.step === REVIEW_STEP
  const draftBlocker = issues.find((issue) => issue.severity === 'error')
  const saveLocked = create.isPending || savedDraftId !== null

  return (
    <SectionFrame route={route} description="Plan every channel, online and offline, with its own tracking, in one flow.">
      <div className="camp-wizard">
        <nav className="camp-stepper" aria-label="Campaign steps">
          <ol>
            {WIZARD_STEPS.map((step, index) => {
              const problems = index !== state.step && index <= state.furthest && issuesFor(issues, step.key).some((issue) => issue.severity !== 'warning')
              return (
                <li key={step.key}>
                  <button
                    type="button"
                    className="camp-stepper__step"
                    aria-current={index === state.step ? 'step' : undefined}
                    data-state={problems ? 'issue' : undefined}
                    disabled={index > state.furthest}
                    onClick={() => dispatch({ type: 'goTo', step: index })}
                  >
                    <span className="camp-stepper__number" aria-hidden="true">
                      {index + 1}
                    </span>
                    {step.label}
                    {problems ? <span className="ci-visually-hidden"> (needs attention)</span> : null}
                  </button>
                </li>
              )
            })}
          </ol>
        </nav>

        <div ref={heading}>
          <Card
            title={
              <span tabIndex={-1} className="camp-inline">
                Step {state.step + 1} of {WIZARD_STEPS.length}: {current.label}
              </span>
            }
            subtitle={STEP_DESCRIPTIONS[stepKey]}
            footer={
              <div className="camp-wizard__footer">
                <button type="button" className="ci-button" disabled={state.step === 0} onClick={() => dispatch({ type: 'back' })}>
                  Back
                </button>
                <div>
                  {draftBlocker ? (
                    <span id="camp-save-draft-reason" className="camp-hint">
                      To save: {draftBlocker.message}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="ci-button"
                    disabled={!canSave(state.draft, 'draft') || saveLocked}
                    aria-describedby={draftBlocker ? 'camp-save-draft-reason' : undefined}
                    onClick={() => void save('draft')}
                  >
                    Save draft
                  </button>
                  {onReview ? (
                    <button
                      type="button"
                      className="ci-button"
                      data-variant="primary"
                      disabled={!canSave(state.draft, 'launch') || saveLocked}
                      onClick={() => void save('launch')}
                    >
                      Launch campaign
                    </button>
                  ) : (
                    <button type="button" className="ci-button" data-variant="primary" onClick={() => dispatch({ type: 'next' })}>
                      Next
                    </button>
                  )}
                </div>
              </div>
            }
          >
            <Body
              state={state}
              dispatch={dispatch}
              issues={issues}
              fieldError={fieldError}
              websites={websiteList ?? []}
              competitions={filterOptions.data?.competition ?? []}
              money={money}
              objectives={objectivesFor(client.kind)}
            />
            {attempted && blockingHere.length > 0 && !onReview ? (
              <div role="alert">
                <IssueList issues={blockingHere} label="Before you continue" />
              </div>
            ) : null}
            {saveError ? (
              <p role="alert" className="camp-error">
                {saveError}{' '}
                {savedDraftId ? (
                  <Link to={{ pathname: `/marketing/campaigns/${encodeURIComponent(savedDraftId)}`, search }}>Finish the saved draft</Link>
                ) : null}
              </p>
            ) : null}
          </Card>
        </div>
      </div>
    </SectionFrame>
  )
}
